import type { NavOrderEntry, NavOrderFile } from '../types';
import { makeNavOrderFile, patchNavOrderIcons, removeNavOrderIcon } from './pageIcon';
import { parentDirOfMarkdown } from './markdownPaths';

export function sanitizeNavOrder(raw: unknown): Record<string, NavOrderEntry[]> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, NavOrderEntry[]> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof k !== 'string' || !Array.isArray(v)) continue;
    const entries: NavOrderEntry[] = [];
    for (const item of v) {
      if (!item || typeof item !== 'object') continue;
      const o = item as { kind?: unknown; path?: unknown; dir?: unknown };
      if (o.kind === 'file' && typeof o.path === 'string') {
        entries.push({ kind: 'file', path: o.path });
      } else if (o.kind === 'dir' && typeof o.dir === 'string') {
        entries.push({ kind: 'dir', dir: o.dir });
      }
    }
    out[k] = entries;
  }
  return out;
}

export function removePathFromNavOrder(file: NavOrderFile, deletedPath: string): NavOrderFile {
  const next: Record<string, NavOrderEntry[]> = {};
  for (const [k, list] of Object.entries(file.order)) {
    const filtered = list.filter(e => {
      if (e.kind === 'file') return e.path !== deletedPath;
      return e.dir !== deletedPath && !e.dir.startsWith(`${deletedPath}/`);
    });
    if (filtered.length > 0) next[k] = filtered;
  }
  return makeNavOrderFile(next, removeNavOrderIcon(file.icons, deletedPath));
}

export function insertPathInNavOrder(file: NavOrderFile, path: string): NavOrderFile {
  const parent = parentDirOfMarkdown(path);
  const order = JSON.parse(JSON.stringify(file.order)) as Record<string, NavOrderEntry[]>;
  const list = [...(order[parent] ?? [])];
  if (!list.some(e => e.kind === 'file' && e.path === path)) {
    list.push({ kind: 'file', path });
  }
  order[parent] = list;
  return makeNavOrderFile(order, file.icons);
}

export function patchNavOrderPathsForRename(
  file: NavOrderFile,
  oldPath: string,
  newPath: string
): NavOrderFile {
  const order = JSON.parse(JSON.stringify(file.order)) as Record<string, NavOrderEntry[]>;
  for (const list of Object.values(order)) {
    for (const e of list) {
      if (e.kind === 'file' && e.path === oldPath) e.path = newPath;
      if (e.kind === 'dir') {
        if (e.dir === oldPath) e.dir = newPath;
        else if (e.dir.startsWith(`${oldPath}/`)) e.dir = newPath + e.dir.slice(oldPath.length);
      }
    }
  }
  return makeNavOrderFile(order, patchNavOrderIcons(file.icons, oldPath, newPath));
}
